import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Docs from './pages/Docs';
import Agents from './pages/Agents';
import DeviceManager from './pages/DeviceManager';
import VehicleLog from './pages/VehicleLog';
import SessionLog from './pages/SessionLog';
import Disputes from './pages/Disputes';
import Customer from './pages/Customer';
import Sidebar from './components/Sidebar';
import { isAuthenticated as checkAuth, forceLogout, refreshAccessToken, migrateLegacyAuthData, isSessionAboutToExpire, isAccessTokenExpired } from './utils/auth';
import './App.css';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(checkAuth());
  const [sessionWarning, setSessionWarning] = useState<boolean>(false);

  useEffect(() => {
    // Migrate any legacy auth data on app start
    migrateLegacyAuthData();

    // Set up session monitoring
    const checkSessionStatus = () => {
      const isAuth = checkAuth();
      setIsAuthenticated(isAuth);

      if (isAuth) {
        // FIRST: Check if access token has actually expired (30 minutes) - FORCE LOGOUT
        if (isAccessTokenExpired()) {
          if (process.env.NODE_ENV === "development") {
            console.log("SuperadminFE: Access token expired (30 minutes), forcing logout");
          }
          setSessionWarning(false); // Hide any warning modal
          forceLogout();
          setIsAuthenticated(false);
          return;
        }

        // SECOND: Check if session is about to expire (2 minutes) - SHOW WARNING
        if (isSessionAboutToExpire()) {
          setSessionWarning(true);
        } else {
          setSessionWarning(false);
        }
      } else {
        // If not authenticated, make sure session warning is hidden
        setSessionWarning(false);
      }
    };

    // Check session status every 30 seconds for better responsiveness
    const interval = setInterval(checkSessionStatus, 30 * 1000);

    // Also check immediately
    checkSessionStatus();

    return () => clearInterval(interval);
  }, []);

  const handleExtendSession = async () => {
    try {
      const success = await refreshAccessToken();
      if (success) {
        setSessionWarning(false);
        setIsAuthenticated(true);
      } else {
        forceLogout();
        setIsAuthenticated(false);
        setSessionWarning(false);
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error('SuperadminFE: Failed to extend session:', error);
      }
      forceLogout();
      setIsAuthenticated(false);
      setSessionWarning(false);
    }
  };

  const handleDismissWarning = () => {
    setSessionWarning(false);
  };

  return (
    <Router>
      <div className="app-container">
        <div className="deprecation-banner">
          <strong>Deprecation Warning:</strong> This production is no longer receiving updates and may contain errors. Please use
          <a
            className="deprecation-link"
            href="https://operator.parkwithparallel.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            operator.parkwithparallel.com
          </a>
          in superadmin mode. This portal will remain active for emergency use cases until no longer needed.
        </div>
        {/* Session Warning Modal */}
        {sessionWarning && (
          <div className="session-warning-overlay">
            <div className="session-warning-modal">
              <h3>Session Expiring Soon</h3>
              <p>Your session will expire in less than 2 minutes. If you don't extend it, you'll be automatically logged out when the session expires.</p>
              <div className="session-warning-buttons">
                <button onClick={handleExtendSession} className="extend-session-btn">
                  Extend Session
                </button>
                <button onClick={handleDismissWarning} className="dismiss-btn">
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        <Routes>
          {/* Login Route */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />}
          />

          {/* Dashboard Route */}
          <Route
            path="/dashboard"
            element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
          />

          <Route
            path="/docs"
            element={isAuthenticated ? <Docs /> : <Navigate to="/login" />}
          />
          <Route
            path="/agents"
            element={isAuthenticated ? <Agents /> : <Navigate to="/login" />}
          />

          {/* Lot Routes */}
          <Route
            path="/lot/:lotId/*"
            element={
              isAuthenticated ? (
                <div className="content-wrapper">
                  <Sidebar />
                  <div className="content">
                    <Routes>
                      <Route path="device-manager" element={<DeviceManager />} />
                      <Route path="vehicle-log" element={<VehicleLog />} />
                      <Route path="session-log" element={<SessionLog />} />
                      <Route path="disputes" element={<Disputes />} />
                      <Route path="customer" element={<Customer />} />
                    </Routes>
                  </div>
                </div>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          {/* Default Route */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
